import {
  ENV_MODE_KEY,
  ENVIRONMENT_MODE,
  LOCAL_STORAGE_APP_CONTEXT_DATA_VAR_NAME,
  LOCAL_STORAGE_I18N_VAR_NAME,
  SEQUENCE_STEPS,
  TEST_MODE_DEFAULT
} from './constants'
import { TEnvironmentMode } from 'utils/interface'

function utf8ToB64(str: string) {
  return btoa(encodeURIComponent(str))
}

export const getEnvSessionKey = (entityId: string, userId: string) => utf8ToB64(`${ENV_MODE_KEY}:${userId}:${entityId}`)

export const getEnvMode = (entityId: string, userId: string): TEnvironmentMode =>
  (sessionStorage.getItem(getEnvSessionKey(entityId, userId)) as any) || 'Production'

export const setEnvMode = (envMode: TEnvironmentMode, entityId: string, userId: string) =>
  sessionStorage.setItem(getEnvSessionKey(entityId, userId), envMode)

export const appLocalStorage = {
  getValue(key: string) {
    if (key) {
      const VALUE = window.localStorage.getItem(key)
      if (VALUE !== null && VALUE !== '') {
        try {
          return JSON.parse(VALUE)
        } catch (e) {
          return VALUE
        }
      }
    }
    return ''
  },
  removeItem(key: string) {
    if (key) {
      window.localStorage.removeItem(key)
    }
  },
  setValue(key: string, value: any) {
    if (key && value) {
      window.localStorage.setItem(key, JSON.stringify(value))
    }
  }
}

export const copyInputValueToClipboard = (inputRef: React.RefObject<HTMLInputElement>): void => {
  const INPUT_CURRENT: HTMLInputElement | null = inputRef.current
  if (INPUT_CURRENT === null) {
    return
  }
  INPUT_CURRENT.select()
  document.execCommand('copy')
}

export const getApiCallSequenceSteps = (actionName: string) =>
  SEQUENCE_STEPS.reduce((object: any, item: any) => {
    const STEP = `${actionName}_${item}`
    object[STEP] = STEP
    return object
  }, {})

export const getCompanyFromLocalStorage = () =>
  appLocalStorage.getValue(LOCAL_STORAGE_APP_CONTEXT_DATA_VAR_NAME).company || {}

export const getCustomUserId = (userId = ''): string =>
  process.env.USE_CUSTOM_USER_ID === 'true' ? process.env.CUSTOM_USER_ID || '' : userId

export const getLangFromLocalStorage = (): string => appLocalStorage.getValue(LOCAL_STORAGE_I18N_VAR_NAME)

export const getTestModeFromEnvironmentMode = (environmentMode: TEnvironmentMode): boolean =>
  environmentMode ? environmentMode === ENVIRONMENT_MODE.sandbox : TEST_MODE_DEFAULT

export const getRandomInt = (max: number): number => Math.floor(Math.random() * Math.floor(max))

export const getSelectOptionsWithDefaultOnTop = (data: any, defaultValue: string) => {
  const DATA = [...data]
  const ITEM_DEFAULT = DATA.find((item: any) => item.label === defaultValue)
  const NEW_DATA = DATA.filter(n => n.label !== defaultValue)

  NEW_DATA.unshift({ label: '──────────────────', value: '', isDisabled: true })
  NEW_DATA.unshift(ITEM_DEFAULT)

  return NEW_DATA
}

export const getUrlParam = (search: string): URLSearchParams => new URLSearchParams(search)

export const IFTTT = (evaluation: boolean, action: any) => {
  if (evaluation) {
    action()
  }
}

export const isInPath = (name?: string) => name && window.location.href.includes(name.toLowerCase())

export const sleep = (milliseconds: number) => new Promise(resolve => setTimeout(resolve, milliseconds))

export const scrollToTop = (main?: boolean) => {
  const PIXELS_TOP_FONT = 10
  const MAIN_REF = document.querySelector('.MainContent')
  const MAIN_OFFSET_TOP = main ? 0 : getOffsetTop(MAIN_REF) - PIXELS_TOP_FONT
  if (MAIN_REF) {
    MAIN_REF.scrollTop = MAIN_OFFSET_TOP
  }
}

export const scrollTopV2 = (): void => {
  const MainElement = document.querySelector('.MainContent')

  if (MainElement) {
    MainElement.scrollTop = 0
  }
}

export const setEntityEnvironmentMode = (
  userId: any,
  companyUpdate: (values: any) => void,
  entityId: string,
  environmentMode: TEnvironmentMode,
  urlToRedirect?: string
): void => {
  setEnvMode(environmentMode, entityId, userId)
  location.assign(urlToRedirect || '/dashboard')
}

const getOffsetTop = (element: any) => {
  let offsetTop = 0
  while (element) {
    offsetTop += element.offsetTop
    element = element.offsetParent
  }
  return offsetTop
}

export const uniqueTemporaryId = (type: string, index: number): string => {
  const time = Date.now()
  const random = Math.floor(1000 + Math.random() * 9000)
  const unique = time + random
  const id = index === undefined ? `${type}-${unique}` : `${type}${index}-${unique}`

  return id
}

export const getParamFromUrlString = (urlString: string, paramName: string): string => {
  let url

  try {
    url = new URL(urlString)
  } catch (error) {
    url = { search: '' }
  }

  const params = new URLSearchParams(url.search)
  const param = params.get(paramName)
  return param ? param : ''
}
